import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { usePaginatedSearch } from './usePaginatedSearch';
import { sortByProperty } from '../utils/array';
import { Order } from '../models/revenue/Order';
import { DEFAULT_PAGE_OPTIONS } from '../utils/paginationUtils';

export interface SortConfig {
  column: string;
  order: Order;
}

export interface UseSortedPaginatedDataOptions<T> {
  data: T[];
  pageOptions?: number[];
}

export interface UseSortedPaginatedDataResult<T> {
  itemsPerPage: number;
  sortConfig: SortConfig;
  sortedData: T[];
  paginatedData: T[];
  handlePaginationChange: (event: CustomEvent<number>) => void;
  handleItemsPerPageChange: (event: CustomEvent<number>) => void;
  handleSortChange: (column: string) => void;
}

export const useSortedPaginatedData = <T>({
  data,
  pageOptions = DEFAULT_PAGE_OPTIONS,
}: UseSortedPaginatedDataOptions<T>): UseSortedPaginatedDataResult<T> => {
  const { page, itemsPerPage, handlePaginationChange, handleItemsPerPageChange, resetToFirstPage } =
    usePaginatedSearch({ pageOptions });

  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: '', order: Order.Ascending });

  const handleSortChange = useCallback(
    (column: string) => {
      setSortConfig((prev) => ({
        column,
        order: prev.column === column && prev.order === Order.Ascending ? Order.Descending : Order.Ascending,
      }));
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    resetToFirstPage();
  }, [data, resetToFirstPage]);

  const sortedData = useMemo(
    () => sortByProperty([...data], sortConfig.column, sortConfig.order),
    [data, sortConfig],
  );

  const paginatedData = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, page, itemsPerPage]);

  return {
    itemsPerPage,
    sortConfig,
    sortedData,
    paginatedData,
    handlePaginationChange,
    handleItemsPerPageChange,
    handleSortChange,
  };
};
